import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { categories, mangaWorks } from '@/mocks/manga';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MangaCard from '@/components/feature/MangaCard';
import { useSEO } from '@/components/base/useSEO';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find(c => c.slug === slug) || categories[0];

  const [filteredWorks, setFilteredWorks] = useState(mangaWorks);
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  useEffect(() => {
    let works = slug === 'all' ? [...mangaWorks] : mangaWorks.filter(w => w.category === slug);

    switch (sortBy) {
      case 'popular':
        works.sort((a, b) => b.views - a.views);
        break;
      case 'rating':
        works.sort((a, b) => b.rating - a.rating);
        break;
      case 'latest':
      default:
        works.sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());
        break;
    }

    setFilteredWorks(works);
  }, [slug, sortBy]);

  const jsonLd = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - 黄漫天堂`,
    description: `${currentCategory.description} · 共${filteredWorks.length}部作品`,
    url: `https://huangman.tt/category/${slug}`,
    inLanguage: 'zh-CN',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://huangman.tt/' },
        { '@type': 'ListItem', position: 2, name: '分类浏览', item: 'https://huangman.tt/category/all' },
        { '@type': 'ListItem', position: 3, name: currentCategory.name, item: `https://huangman.tt/category/${slug}` },
      ],
    },
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: filteredWorks.slice(0, 12).map((w, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'Article',
          name: w.title,
          author: { '@type': 'Person', name: w.author },
          url: `https://huangman.tt/article/${w.id}`,
          datePublished: w.updateDate,
        },
      })),
    },
  });

  useSEO({
    title: `${currentCategory.name} - 黄漫天堂`,
    description: `${currentCategory.description}。浏览${currentCategory.name}分类下的全部漫画作品，按最新更新、最多阅读或最高评分排序。`,
    keywords: `${currentCategory.name},黄漫天堂,${currentCategory.name}漫画,${currentCategory.name}推荐`,
    canonical: `https://huangman.tt/category/${slug}`,
    jsonLd,
  });

  const sortOptions = [
    { value: 'latest' as const, label: '最新更新' },
    { value: 'popular' as const, label: '最多阅读' },
    { value: 'rating' as const, label: '最高评分' },
  ];

  return (
    <main className="min-h-screen pt-16">
      <div className="bg-background-100/70 border-b border-background-200/70">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <Breadcrumb items={[
            { label: '首页', href: '/' },
            { label: '分类浏览', href: '/category/all' },
            { label: currentCategory.name },
          ]} />
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-8">
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-2">
            {currentCategory.name}
          </h1>
          <p className="text-foreground-500 text-sm">
            {currentCategory.description} · 共{filteredWorks.length}部作品
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          <aside className="lg:w-[200px] shrink-0">
            <nav className="flex lg:flex-col gap-1.5 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0" aria-label="分类导航">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    cat.slug === slug
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
                  }`}
                >
                  <span className="w-6 h-6 flex items-center justify-center">
                    <i className={`${cat.icon} text-xs`}></i>
                  </span>
                  {cat.name}
                  <span className="text-[10px] text-foreground-400 ml-auto">{cat.count}</span>
                </Link>
              ))}
            </nav>
          </aside>

          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
                {sortOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setSortBy(opt.value)}
                    className={`px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      sortBy === opt.value
                        ? 'bg-background-50 text-foreground-900'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {filteredWorks.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
                {filteredWorks.map((work) => (
                  <MangaCard key={work.id} {...work} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-inbox-line text-2xl"></i>
                </div>
                <p className="text-foreground-500">该分类暂无作品</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}